import { Mail, Phone, MapPin, Send } from "lucide-react";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import StructuredData from "./StructuredData";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Using EmailJS or similar service
      // For now, we'll use a mailto link as fallback
      const mailtoLink = `mailto:contact@bankelys.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
        `Nom: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
      )}`;
      
      window.location.href = mailtoLink;
      
      setSubmitStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "FinancialService",
      "name": "Bankelys",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "93 Rue de La Palestine, Beb El Bhar",
        "addressLocality": "Tunis",
        "postalCode": "1002",
        "addressCountry": "TN"
      },
      "telephone": "+216 98 222 635",
      "email": "nabil.elmadani@bankelys.com",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
          "opens": "09:00",
          "closes": "17:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "09:00",
          "closes": "13:00"
        }
      ],
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "36.7948",
        "longitude": "10.1800"
      }
    }
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://bankelys.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Contact",
        "item": "https://bankelys.com/contact"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Contactez-nous — Bankelys Conseil Financier Tunis</title>
        <meta name="description" content="Contactez Bankelys à Tunis pour vos projets financiers. 93 Rue de La Palestine, Tunis 1002. Tél : +216 98 222 635. Réponse sous 24h." />
        <link rel="canonical" href="https://bankelys.com/contact" />
        <meta property="og:title" content="Contactez Bankelys — Conseil Financier à Tunis" />
        <meta property="og:description" content="Prenez rendez-vous avec nos experts financiers. Bureau au centre-ville de Tunis. Réponse rapide garantie." />
        <meta property="og:url" content="https://bankelys.com/contact" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Contactez Bankelys — Experts Finance à Tunis" />
        <meta name="twitter:description" content="Besoin d'un conseil financier ? Contactez Bankelys au +216 98 222 635 ou via notre formulaire." />
      </Helmet>
      <StructuredData data={contactData} />
      <StructuredData data={breadcrumbData} />

      {/* Hero Section - Matching Notre Équipe */}
      <section className="relative h-[40vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/slide7.png"
            alt="Contactez l'équipe Bankelys — conseil financier à Tunis"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center px-6 md:px-10">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-light mb-4 leading-tight text-white">
            Contactez-nous
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
            Notre équipe est à votre écoute pour répondre à toutes vos questions et vous accompagner dans vos projets financiers
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-24 px-6 md:px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl md:text-5xl font-light text-[#0B2545] mb-6">
                Informations de contact
              </h2>
              <p className="text-[15px] text-[#666] leading-[1.8] mb-12">
                N'hésitez pas à nous contacter par téléphone, email ou en remplissant le formulaire. 
                Nous vous répondrons dans les plus brefs délais.
              </p>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#F7F9FB] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#0B2545]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-[#0B2545] mb-2">Adresse</h3>
                    <p className="text-[15px] text-[#666] leading-[1.8]">
                      93 Rue de La Palestine<br />
                      Beb El Bhar, Tunis 1002<br />
                      Tunisie
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#F7F9FB] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#0B2545]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-[#0B2545] mb-2">Téléphone</h3>
                    <a
                      href="tel:+21698222635"
                      className="text-[15px] text-[#666] hover:text-[#0B2545] transition-colors duration-300 block"
                    >
                      +216 98 222 635
                    </a>
                    <a
                      href="tel:+21622515074"
                      className="text-[15px] text-[#666] hover:text-[#0B2545] transition-colors duration-300 block"
                    >
                      +216 22 515 074
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#F7F9FB] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#0B2545]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-[#0B2545] mb-2">Email</h3>
                    <a 
                      href="mailto:nabil.elmadani@bankelys.com" 
                      className="text-[15px] text-[#666] hover:text-[#0B2545] transition-colors duration-300"
                    >
                      nabil.elmadani@bankelys.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Office Hours */}
              <div className="mt-12 p-8 bg-[#F7F9FB] rounded-2xl">
                <h3 className="text-lg font-medium text-[#0B2545] mb-4">Horaires d'ouverture</h3>
                <div className="space-y-2 text-[15px] text-[#666]">
                  <div className="flex justify-between">
                    <span>Lundi - Vendredi</span>
                    <span className="font-medium">9h00 - 17h00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Samedi</span>
                    <span className="font-medium">9h00 - 13h00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Dimanche</span>
                    <span className="font-medium">Fermé</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white rounded-2xl shadow-lg p-10 border border-gray-100">
                <h2 className="text-2xl font-light text-[#0B2545] mb-8">
                  Envoyez-nous un message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label 
                      htmlFor="name" 
                      className="block text-[14px] font-medium text-[#0B2545] mb-2"
                    >
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B2545] focus:border-transparent transition-all duration-300 outline-none"
                      placeholder="Votre nom"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label 
                      htmlFor="email" 
                      className="block text-[14px] font-medium text-[#0B2545] mb-2"
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B2545] focus:border-transparent transition-all duration-300 outline-none"
                      placeholder="votre@email.com"
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label 
                      htmlFor="subject" 
                      className="block text-[14px] font-medium text-[#0B2545] mb-2"
                    >
                      Sujet *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B2545] focus:border-transparent transition-all duration-300 outline-none"
                      placeholder="Objet de votre message"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label 
                      htmlFor="message" 
                      className="block text-[14px] font-medium text-[#0B2545] mb-2"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B2545] focus:border-transparent transition-all duration-300 outline-none resize-none"
                      placeholder="Votre message..."
                    ></textarea>
                  </div>

                  {/* Status Messages */}
                  {submitStatus === "success" && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                      Message envoyé avec succès ! Nous vous répondrons bientôt.
                    </div>
                  )}
                  
                  {submitStatus === "error" && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      Une erreur est survenue. Veuillez réessayer.
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#0B2545] hover:bg-[#0a1f3a] text-white font-medium text-[15px] px-8 py-4 rounded-full transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <span>Envoyer le message</span>
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Map Section (Optional) */}
      <section className="h-[400px] bg-gray-200">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3193.3841991756647!2d10.180815!3d36.804924!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDQ4JzE3LjciTiAxMMKwMTAnNTEuMyJF!5e0!3m2!1sen!2stn!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Emplacement du bureau Bankelys — 93 Rue de La Palestine, Tunis"
        ></iframe>
      </section>
    </div>
  );
}